// Последовательность старта SCR-BOOT (E02-T06; ux/11 §3.1; ux/02 §5.1; tech/01 §6.1).
// Порядок проверок: версия Telegram и WebGL → /meta (техработы, минимальная сборка) → вход по initData →
// bootstrap → маршрут: незаконченный бой / непоказанный итог / обучение / SCR-HOME.
// Пороги: 8 с — «Медленная сеть, ещё немного…», 20 с — «Не грузится. Проверь связь» и «Повторить».
import { signal } from '@preact/signals';
import type { App } from '../app/app.js';
import { CLIENT_BUILD } from '../app/app.js';
import { track } from '../analytics/analytics.js';
import { ApiCallError } from '../net/api-client.js';
import { login } from '../net/session.js';
import { applyBootstrap, loadCache, online, tutorialActive } from '../state/game.js';
import { storage } from '../state/storage.js';
import { MIN_BOT_API, versionAtLeast, versionKnown, type TgWebApp } from '../telegram/webapp.js';
import { startTutorial } from '../tutorial/flow.js';
import { t as tr } from '../i18n/t.js';

export const BOOT_SLOW_MS = 8000;
export const BOOT_STUCK_MS = 20_000;
export const MAINT_RETRY_MS = 30_000;

export const bootUi = signal<{ progress: number; slow: boolean; stuck: boolean; error: string | null }>({ progress: 0.1, slow: false, stuck: false, error: null });

const t0 = performance.timeOrigin ? Date.now() - performance.now() : Date.now();
export function loadStep(step: 'html' | 'entry_js' | 'auth' | 'bootstrap' | 'atlas_core' | 'first_screen', bytes = 0): void {
  track('load_step', { step, ms_since_open: Math.round(Date.now() - t0), bytes });
}

/** Сравнение версий сборки «0.1.0» < «0.2.0». */
export function buildLess(a: string, b: string): boolean {
  const x = a.split(/[.+-]/).map(Number);
  const y = b.split(/[.+-]/).map(Number);
  for (let i = 0; i < 3; i++) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d) return d < 0;
  }
  return false;
}

export function hasWebgl(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') ?? c.getContext('webgl'));
  } catch { return false; }
}

export const LAST_BATTLE_KEY = 'lastBattle';
export interface LastBattle { battleId: string; seen: boolean; origin?: string }

export interface BootCtx { initData: string | null; wa: TgWebApp | null; cold: boolean }

let running = false;

export async function runBoot(app: App, ctx: BootCtx): Promise<void> {
  if (running) return;
  running = true;
  const { nav } = app;
  nav.showSystem('SCR-BOOT', {});
  bootUi.value = { progress: 0.15, slow: false, stuck: false, error: null };
  const started = Date.now();
  const slow = setTimeout(() => { bootUi.value = { ...bootUi.value, slow: true }; }, BOOT_SLOW_MS);
  const stuck = setTimeout(() => { bootUi.value = { ...bootUi.value, stuck: true }; }, BOOT_STUCK_MS);
  const done = () => { clearTimeout(slow); clearTimeout(stuck); running = false; };
  try {
    if (ctx.wa && versionKnown() && !versionAtLeast(ctx.wa, MIN_BOT_API)) { nav.showSystem('SCR-UNSUPPORTED', { reason: 'telegram' }); return; }
    if (!hasWebgl()) { nav.showSystem('SCR-UNSUPPORTED', { reason: 'webgl' }); return; }
    const hasCache = loadCache();

    let meta;
    try {
      meta = await app.call('getMeta');
    } catch (e) {
      if (e instanceof ApiCallError && (e.code === 'NETWORK' || e.code === 'TIMEOUT')) {
        online.value = false;
        if (hasCache) { nav.hideSystem(); waitOnline(app, ctx); return; } // SCR-HOME в ST-OFFLINE
        nav.showSystem('SCR-NO-NETWORK', {});
        return;
      }
      throw e;
    }
    bootUi.value = { ...bootUi.value, progress: 0.35 };
    if (meta.maintenance && Date.parse(meta.maintenance.to) > Date.now()) {
      nav.showSystem('SCR-MAINTENANCE', { to: Date.parse(meta.maintenance.to) });
      return;
    }
    if (buildLess(CLIENT_BUILD, meta.minBuild)) { nav.showSystem('SCR-UPDATE-REQUIRED', {}); return; }
    if (!ctx.initData) {
      nav.showSystem('SCR-BOOT', { code: 'INIT_DATA_INVALID', text: tr('CL-BOOT-BOOT-01') });
      return;
    }

    const auth = await login(app.call, app.session, ctx.initData, { platform: ctx.wa?.platform ?? 'web', tgVersion: ctx.wa?.version ?? '0' });
    loadStep('auth');
    bootUi.value = { ...bootUi.value, progress: 0.7 };
    void app.reporter.flush();
    const boot = auth.bootstrap ?? await app.call('getBootstrap');
    loadStep('bootstrap');
    applyBootstrap(boot);
    // Список согласий — каждый вход заново: сервер снял требование — плашка «Правила обновились» тоже уходит.
    if (auth.consentsRequired.length) storage.setJson('consentsRequired', auth.consentsRequired); else storage.remove('consentsRequired');
    bootUi.value = { ...bootUi.value, progress: 1 };
    track('boot_done', { ms: Date.now() - started, isNew: auth.isNew });

    // Маршрут после входа (ux/02 §5.1).
    nav.hideSystem();
    // Обучение — раньше активного боя: незаконченный учебный бой B01-T подхватывается потоком обучения,
    // чтобы ушли шаги T03/T04 и после победы открылись трофеи (ГД 12 §5, восстановление с шага сервера).
    if (tutorialActive(boot.tutorialStep)) {
      startTutorial(app, boot.tutorialStep, boot.activeBattle);
    } else if (boot.activeBattle) {
      nav.selectTab('map');
      nav.push('SCR-FIGHT', { state: boot.activeBattle, restored: true });
    } else if (!(await showUnseenResult(app))) {
      openDeepLink(app, ctx.wa?.initDataUnsafe?.start_param ?? null);
    }
    loadStep('first_screen');
  } catch (e) {
    if (e instanceof ApiCallError) {
      if (e.code === 'MAINTENANCE') {
        const to = typeof e.details?.['to'] === 'string' ? Date.parse(e.details['to']) : Date.now() + (e.retryAfterS ?? 60) * 1000;
        nav.showSystem('SCR-MAINTENANCE', { to });
      } else if (e.code === 'NETWORK' || e.code === 'TIMEOUT') {
        nav.showSystem('SCR-NO-NETWORK', {});
      } else {
        const p = app.handleError(e);
        if (p.surface !== 'system' && p.surface !== 'modal') nav.showSystem('SCR-BOOT', { code: e.code, text: tr('CL-BOOT-BOOT-02'), retry: true });
      }
    } else {
      app.reporter.report('js', e, { fatal: true });
    }
  } finally {
    done();
  }
}

/** Нет сети при старте, но есть кэш: SCR-HOME в ST-OFFLINE, повтор входа при событии online. */
function waitOnline(app: App, ctx: BootCtx) {
  const retry = () => { removeEventListener('online', retry); void runBoot(app, ctx); };
  addEventListener('online', retry);
  setTimeout(retry, 15_000);
}

/** Диплинк (ux/02 §6): `p_<publicId>` — чужой гараж; прочие — с экранами своих этапов. */
export function openDeepLink(app: App, startParam: string | null): boolean {
  const m = startParam?.match(/^p_([0-9A-HJ-NP-Z]{8})$/);
  if (m) { app.nav.push('SCR-GARAGE-VISIT', { publicId: m[1] }); return true; }
  // Приглашение в кооператив (ux/02 §6): coop_<id> → вкладка кооператива и карточка поверх.
  const c = startParam?.match(/^coop_([0-9]{1,19})$/);
  if (!c) return false;
  app.nav.selectTab('coop');
  app.nav.openSheet('MOD-COOP-PREVIEW', { coopId: c[1] });
  return true;
}

/** «Обрыв на итоге → при возвращении итог показывается заново один раз» (E03-T06). */
export async function showUnseenResult(app: App): Promise<boolean> {
  const lb = storage.getJson<LastBattle>(LAST_BATTLE_KEY);
  if (!lb || lb.seen) return false;
  try {
    const r = await app.call('getBattle', { params: { battleId: lb.battleId } });
    app.nav.selectTab('map');
    app.nav.push('SCR-FIGHT-RESULT', { result: r, bossId: lb.origin });
    return true;
  } catch {
    storage.setJson(LAST_BATTLE_KEY, { ...lb, seen: true });
    return false;
  }
}

/** Повторный вход: SIGNATURE_INVALID один раз — тихий повторный вход по initData (ux/11 §4.1). */
export function makeRelogin(app: App, ctx: BootCtx) {
  let used = false;
  return async (): Promise<boolean> => {
    if (used || !ctx.initData) return false;
    used = true;
    try {
      await login(app.call, app.session, ctx.initData, { platform: ctx.wa?.platform ?? 'web', tgVersion: ctx.wa?.version ?? '0' });
      return true;
    } catch { return false; }
  };
}
